"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { showToast } from "@/src/components/toast/showToast";
import { post } from "@/src/api/apiHandler";
import { convertPersianToEnglish } from "@/src/tools";
import { useState } from "react";
import { ValidateContact } from "./tools";
import { Loader2 } from "lucide-react";

const FormContact = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobile, setMobile] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const SubmitContact = async (e: React.FormEvent) => {
    e.preventDefault();
    const validate = ValidateContact(firstName, lastName, mobile, description);
    if (validate !== "") {
      showToast(validate, "info");
      return false;
    } else {
      try {
        setLoading(true);
        await post("/form", {
          firstName,
          lastName,
          mobile,
          description,
        });
        setFirstName("");
        setLastName("");
        setMobile("");
        setDescription("");
        showToast("با موفقیت ارسال شد", "success");
      } catch {
        showToast("مشکلی پیش آمده دوباره تلاش کنید", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = e.target;
    const reg = /^[0-9\u06F0-\u06F9]+$/;
    if (reg.test(inputValue) || inputValue === "" || inputValue === "-") {
      setMobile(convertPersianToEnglish(inputValue));
    }
  };

  return (
    <form onSubmit={SubmitContact} className="mt-10" dir="rtl">
      <div className="grid gap-5 sm:grid-cols-2 sm:gap-5">
        <div className="relative">
          <label htmlFor="first-name" className="sr-only">
            نام
          </label>
          <Input
            className="h-12 rounded-lg border-[#cfd3d8] bg-white px-4 text-right text-xs text-[#2b3038] shadow-none placeholder:text-[#8c929b] focus-visible:border-[#1763e7] focus-visible:ring-2 focus-visible:ring-[#1763e7]/15 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-100"
            onChange={(e) => setFirstName(e.target.value)}
            value={firstName}
            type="text"
            id="first-name"
            placeholder=" "
            aria-label="نام"
            required
          />
          {!firstName ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-4 flex items-center gap-1 text-xs text-[#8c929b]"
            >
              نام <span className="text-[#e34848]">*</span>
            </span>
          ) : null}
        </div>

        <div className="relative">
          <label htmlFor="last-name" className="sr-only">
            نام خانوادگی
          </label>
          <Input
            className="h-12 rounded-lg border-[#cfd3d8] bg-white px-4 text-right text-xs text-[#2b3038] shadow-none placeholder:text-[#8c929b] focus-visible:border-[#1763e7] focus-visible:ring-2 focus-visible:ring-[#1763e7]/15 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-100"
            onChange={(e) => setLastName(e.target.value)}
            value={lastName}
            type="text"
            id="last-name"
            placeholder=" "
            aria-label="نام خانوادگی"
            required
          />
          {!lastName ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-4 flex items-center gap-1 text-xs text-[#8c929b]"
            >
              نام خانوادگی <span className="text-[#e34848]">*</span>
            </span>
          ) : null}
        </div>
      </div>

      <div className="relative mt-5">
        <label htmlFor="mobile" className="sr-only">
          شماره موبایل
        </label>
        <Input
          className="h-12 rounded-lg border-[#cfd3d8] bg-white px-4 text-right text-xs text-[#2b3038] shadow-none placeholder:text-[#8c929b] focus-visible:border-[#1763e7] focus-visible:ring-2 focus-visible:ring-[#1763e7]/15 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-100"
          name="mobile"
          id="mobile"
          placeholder=" "
          aria-label="شماره موبایل"
          maxLength={11}
          value={mobile}
          onChange={handleChange}
          inputMode="numeric"
          required
        />
        {!mobile ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-4 flex items-center gap-1 text-xs text-[#8c929b]"
          >
            شماره موبایل <span className="text-[#e34848]">*</span>
          </span>
        ) : null}
      </div>

      <div className="mt-5">
        <label htmlFor="description" className="sr-only">
          پیام شما
        </label>
        <Textarea
          className="h-[250px] resize-none rounded-lg border-[#cfd3d8] bg-white px-4 py-4 text-right text-xs leading-6 text-[#2b3038] shadow-none placeholder:text-[#8c929b] focus-visible:border-[#1763e7] focus-visible:ring-2 focus-visible:ring-[#1763e7]/15 sm:h-[320px] dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-100"
          onChange={(e) => setDescription(e.target.value)}
          value={description}
          id="description"
          placeholder="پیام شما"
          aria-label="پیام شما"
          required
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="mx-auto mt-10 flex h-12 min-w-[134px] rounded-lg bg-[#145cc9] px-5 text-xs font-bold text-white shadow-none transition hover:bg-[#0f4fae] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            در حال ارسال...
          </span>
        ) : (
          "ارسال پیام"
        )}
      </Button>
    </form>
  );
};

export default FormContact;
